import { useState, useRef, useEffect } from 'react'
import { apiError } from '../lib/errors'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, X, ChevronDown, CalendarDays, Dna, Sparkles, TrendingUp, TrendingDown, RefreshCw, Check, Tag } from 'lucide-react'
import { varietiesApi, api, pricingApi, PLANT_PURPOSES, type Variety, type PriceRecommendation } from '../lib/api'

// ── Growable reference types ───────────────────────────────────────────────
interface GrowableRef {
  id: string
  common_name: string
  latin_name: string
  scientific_name: string
  family: string
  category: string
  flavor_profile: string
  aliases_es: string
  aliases_other: string
  aliases_common: string[]
  ripe_months: number[]
  tags: string[]
}

// Fetch all growable reference entries for autocomplete
function useGrowableRef() {
  return useQuery<GrowableRef[]>({
    queryKey: ['growable-reference'],
    queryFn: () => api.get<{ reference: GrowableRef[] }>('/varieties/reference').then(r => r.data.reference ?? []),
    staleTime: 10 * 60 * 1000, // 10 min — reference data rarely changes
  })
}

const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     '#1A2314',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  text:      'var(--color-text)',
  muted:     'var(--color-text-muted)',
  faint:     'var(--color-text-faint)',
  danger:    '#F87171',
  hover:     'rgba(255,255,255,0.04)',
}
const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.muted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}

const CATEGORIES = ['Mango','Avocado','Citrus','Lychee','Tropical','Palm','Berry','Herb','Vegetable','Other']
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const CAT_COLORS: Record<string, string> = {
  Mango:      '#F5A623',
  Avocado:    '#4CAF50',
  Citrus:     '#FF9800',
  Lychee:     '#E91E63',
  Tropical:   '#00BCD4',
  Palm:       '#8BC34A',
  Berry:      '#9C27B0',
  Herb:       '#009688',
  Vegetable:  '#3F51B5',
  Other:      '#607D8B',
}

/** week-of-year (1-52) → month index (0-11) */
function weekToMonthFrac(week: number) { return ((week - 1) / 52) * 12 }

// ── CommonNameAutocomplete ─────────────────────────────────────────────────
interface ACProps {
  value: string
  onChange: (val: string) => void
  onSelect: (ref: GrowableRef) => void
  refs: GrowableRef[]
  style?: React.CSSProperties
}

function CommonNameAutocomplete({ value, onChange, onSelect, refs, style }: ACProps) {
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(-1)
  const wrapRef = useRef<HTMLDivElement>(null)

  const q = value.toLowerCase().trim()
  const matches = q.length < 1 ? [] : refs.filter(r => {
    if (r.common_name.toLowerCase().includes(q)) return true
    if (r.aliases_common.some(a => a.toLowerCase().includes(q))) return true
    if (r.aliases_es.toLowerCase().includes(q)) return true
    if (r.aliases_other.toLowerCase().includes(q)) return true
    if (r.latin_name.toLowerCase().includes(q)) return true
    return false
  }).slice(0, 10)

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function handleKey(e: React.KeyboardEvent) {
    if (!open || matches.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(f => Math.min(f + 1, matches.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFocused(f => Math.max(f - 1, 0)) }
    if (e.key === 'Enter' && focused >= 0) { e.preventDefault(); onSelect(matches[focused]); setOpen(false); setFocused(-1) }
    if (e.key === 'Escape') setOpen(false)
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        style={style}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setFocused(-1) }}
        onFocus={() => { if (value.length > 0) setOpen(true) }}
        onKeyDown={handleKey}
        placeholder="Mango, Carambola, Soursop…"
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1100,
          background: '#1A2314', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8, marginTop: 2, overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}>
          {matches.map((ref, i) => (
            <button key={ref.id} type="button"
              onMouseEnter={() => setFocused(i)}
              onClick={() => { onSelect(ref); setOpen(false); setFocused(-1) }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', border: 'none', cursor: 'pointer',
                background: i === focused ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)' : 'transparent',
                color: i === focused ? '#F5A623' : 'var(--color-text)',
                transition: 'background 0.1s',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>{ref.common_name}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 1 }}>
                {ref.latin_name || ref.scientific_name}
                {(ref.aliases_es || ref.aliases_other) && (
                  <span style={{ marginLeft: 8, opacity: 0.75 }}>
                    · {[ref.aliases_es, ref.aliases_other].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── VarietyModal ───────────────────────────────────────────────────────────
function VarietyModal({ variety, onClose }: { variety: Variety | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isEdit = !!variety
  const { data: refData = [] } = useGrowableRef()
  const [form, setForm] = useState({
    name: variety?.name ?? '', common_name: variety?.common_name ?? '',
    species: variety?.species ?? '', category: variety?.category ?? 'Mango',
    nursery_source: variety?.nursery_source ?? '', flavor_profile: variety?.flavor_profile ?? '',
    notes: variety?.notes ?? '',
    ripening_start_week: variety?.ripening_start_week?.toString() ?? '',
    ripening_end_week: variety?.ripening_end_week?.toString() ?? '',
    average_weight_grams: variety?.average_weight_grams?.toString() ?? '',
    purpose: variety?.purpose ?? '',
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  // Auto-populate from growable_reference selection
  function applyRef(ref: GrowableRef) {
    setForm(f => ({
      ...f,
      common_name:   ref.common_name,
      species:       ref.scientific_name || ref.latin_name || f.species,
      flavor_profile: ref.flavor_profile || f.flavor_profile,
    }))
  }

  const save = useMutation({
    mutationFn: (data: Partial<Variety>) =>
      (isEdit ? varietiesApi.update(variety!.id, data) : varietiesApi.create(data)) as Promise<unknown>,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['varieties'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Variety name is required'); return }
    save.mutate({
      name: form.name, common_name: form.common_name || undefined,
      species: form.species || undefined, category: form.category,
      nursery_source: form.nursery_source || undefined, flavor_profile: form.flavor_profile || undefined,
      notes: form.notes || undefined, purpose: form.purpose || undefined,
      ripening_start_week: form.ripening_start_week ? parseInt(form.ripening_start_week) : undefined,
      ripening_end_week: form.ripening_end_week ? parseInt(form.ripening_end_week) : undefined,
      average_weight_grams: form.average_weight_grams ? parseInt(form.average_weight_grams) : undefined,
    })
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 480, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{isEdit ? 'Edit variety' : 'Add variety'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Variety name *</label><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Keitt" required /></div>
            <div>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Dna size={10} style={{ opacity: 0.6 }} />
                Common name
                <span style={{ fontWeight: 400, color: C.faint, marginLeft: 2 }}>— autocomplete</span>
              </label>
              <CommonNameAutocomplete
                value={form.common_name}
                onChange={v => set('common_name', v)}
                onSelect={applyRef}
                refs={refData}
                style={inp}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Species</label><input style={inp} value={form.species} onChange={e => set('species', e.target.value)} placeholder="Mangifera indica" /></div>
            <div>
              <label style={lbl}>Category</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label style={lbl}>Nursery source</label><input style={inp} value={form.nursery_source} onChange={e => set('nursery_source', e.target.value)} placeholder="Pine Island Nursery" /></div>
          <div>
            <label style={lbl}>Default purpose</label>
            <select style={{ ...inp, cursor: 'pointer' }} value={form.purpose} onChange={e => set('purpose', e.target.value)}>
              {PLANT_PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Ripening start (wk)</label><input style={inp} type="number" min={1} max={52} value={form.ripening_start_week} onChange={e => set('ripening_start_week', e.target.value)} placeholder="30" /></div>
            <div><label style={lbl}>Ripening end (wk)</label><input style={inp} type="number" min={1} max={52} value={form.ripening_end_week} onChange={e => set('ripening_end_week', e.target.value)} placeholder="40" /></div>
            <div><label style={lbl}>Avg weight (g)</label><input style={inp} type="number" min={1} value={form.average_weight_grams} onChange={e => set('average_weight_grams', e.target.value)} placeholder="680" /></div>
          </div>
          <div><label style={lbl}>Flavor profile</label><input style={inp} value={form.flavor_profile} onChange={e => set('flavor_profile', e.target.value)} placeholder="Sweet, mild, low-fiber" /></div>
          <div><label style={lbl}>Notes</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Disease resistance, growing tips…" /></div>
          {error && <div style={{ fontSize: 12, color: C.danger, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, cursor: save.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add variety'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** Ripening Gantt chart — 12-month axis, one row per variety */
function RipeningCalendar({ varieties }: { varieties: Variety[] }) {
  const withWindows = varieties.filter(v => v.ripening_start_week && v.ripening_end_week)
  if (withWindows.length === 0) return null

  const now = new Date()
  const currentMonthFrac = now.getMonth() + now.getDate() / 30

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <CalendarDays size={15} style={{ color: C.amber }} />
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Ripening Calendar</h2>
        <span style={{ fontSize: 11, color: C.faint, marginLeft: 4 }}>{withWindows.length} varieties with windows</span>
      </div>

      {/* Month header */}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 0, marginBottom: 6 }}>
        <div />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', position: 'relative' }}>
          {MONTHS.map((m, i) => (
            <div key={m} style={{ fontSize: 10, color: i === now.getMonth() ? C.amber : C.faint, fontWeight: i === now.getMonth() ? 700 : 400, textAlign: 'center' }}>{m}</div>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {withWindows.sort((a, b) => (a.ripening_start_week ?? 0) - (b.ripening_start_week ?? 0)).map(v => {
          const startFrac = weekToMonthFrac(v.ripening_start_week!)
          const endFrac   = weekToMonthFrac(v.ripening_end_week! + 1)
          const leftPct   = (startFrac / 12) * 100
          const widthPct  = Math.max(((endFrac - startFrac) / 12) * 100, 2)
          const color = CAT_COLORS[v.category] ?? C.muted

          return (
            <div key={v.id} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 0, alignItems: 'center' }}>
              <div style={{ paddingRight: 10, overflow: 'hidden' }}>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</p>
                {v.average_weight_grams && <p style={{ margin: 0, fontSize: 10, color: C.faint }}>{v.average_weight_grams}g</p>}
              </div>
              <div style={{ position: 'relative', height: 22, background: 'rgba(255,255,255,0.03)', borderRadius: 4, overflow: 'hidden' }}>
                {/* Today line */}
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${(currentMonthFrac / 12) * 100}%`, width: 1, background: C.amber, opacity: 0.5, zIndex: 2 }} />
                {/* Ripening bar */}
                <div style={{ position: 'absolute', top: 3, bottom: 3, left: `${leftPct}%`, width: `${widthPct}%`, background: color, borderRadius: 3, opacity: 0.85, zIndex: 1 }} title={`${v.name}: wk ${v.ripening_start_week}–${v.ripening_end_week}`} />
              </div>
            </div>
          )
        })}
      </div>

      {/* Month grid lines overlay hint */}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', marginTop: 6 }}>
        <div />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 0 }}>
          {MONTHS.map((_, i) => (
            <div key={i} style={{ borderLeft: `1px solid ${C.border}`, height: 6 }} />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
        {Array.from(new Set(withWindows.map(v => v.category))).map(cat => (
          <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: CAT_COLORS[cat] ?? C.muted }} />
            <span style={{ fontSize: 11, color: C.muted }}>{cat}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 1, height: 12, background: C.amber, opacity: 0.7 }} />
          <span style={{ fontSize: 11, color: C.faint }}>Today</span>
        </div>
      </div>
    </div>
  )
}

// ── PricingPanel ───────────────────────────────────────────────────────────
// Per-variety DEFAULT retail/wholesale prices ($/kg). AI estimates them regionally
// (fills empty defaults the first time), and a recommendation engine suggests
// quarterly up/down adjustments WITHOUT changing the defaults until you Apply.
const centsToStr = (c?: number | null) => (c == null ? '' : (c / 100).toFixed(2))
const strToCents = (s: string) => { const n = parseFloat(s); return isNaN(n) ? null : Math.round(n * 100) }

function PricingPanel() {
  const qc = useQueryClient()
  const [drafts, setDrafts] = useState<Record<string, { retail: string; wholesale: string }>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [open, setOpen] = useState(true)

  const { data: pricesData, isLoading } = useQuery({ queryKey: ['pricing'], queryFn: () => pricingApi.list().then(r => r.data) })
  const { data: recsData } = useQuery({ queryKey: ['price-recs'], queryFn: () => pricingApi.recommendations('pending').then(r => r.data) })
  const prices = pricesData?.prices ?? []
  const recs = recsData?.recommendations ?? []
  const region = prices.find(p => p.region)?.region ?? ''

  const setPrice = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { retail_cents_per_kg?: number | null; wholesale_cents_per_kg?: number | null } }) => pricingApi.setPrice(id, body),
    onSuccess: (_d, v) => { setDrafts(d => { const n = { ...d }; delete n[v.id]; return n }); qc.invalidateQueries({ queryKey: ['pricing'] }) },
  })
  const estimateOne = useMutation({
    mutationFn: (id: string) => pricingApi.estimate(id, region || undefined).then(r => r.data),
    onSuccess: (res) => { setNotice(res.warning || `Estimated ${res.estimates.length} variety price${res.estimates.length === 1 ? '' : 's'}${res.seeded ? ` · filled ${res.seeded} empty default${res.seeded === 1 ? '' : 's'}` : ''}.`); qc.invalidateQueries({ queryKey: ['pricing'] }) },
    onError: (e) => setNotice(apiError(e, 'Estimate failed.')),
  })
  const estimateMissing = useMutation({
    mutationFn: () => pricingApi.estimateMissing(region || undefined).then(r => r.data),
    onSuccess: (res) => { setNotice(res.warning || `Filled ${res.seeded} empty default${res.seeded === 1 ? '' : 's'} across ${res.estimates.length} varieties${res.region ? ` for ${res.region}` : ''}.`); qc.invalidateQueries({ queryKey: ['pricing'] }) },
    onError: (e) => setNotice(apiError(e, 'Estimate failed.')),
  })
  const refreshRecs = useMutation({
    mutationFn: () => pricingApi.refreshRecommendations(region || undefined).then(r => r.data),
    onSuccess: (res) => { setNotice(res.warning || (res.created > 0 ? `${res.created} price recommendation${res.created === 1 ? '' : 's'} ready to review.` : 'No adjustments recommended — your prices look current.')); qc.invalidateQueries({ queryKey: ['price-recs'] }) },
    onError: (e) => setNotice(apiError(e, 'Refresh failed.')),
  })
  const decideRec = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'apply' | 'dismiss' }) => action === 'apply' ? pricingApi.applyRecommendation(id) : pricingApi.dismissRecommendation(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['price-recs'] }); qc.invalidateQueries({ queryKey: ['pricing'] }) },
  })

  const draftFor = (vid: string, retail?: number | null, wholesale?: number | null) =>
    drafts[vid] ?? { retail: centsToStr(retail), wholesale: centsToStr(wholesale) }
  const setDraft = (vid: string, k: 'retail' | 'wholesale', val: string, retail?: number | null, wholesale?: number | null) =>
    setDrafts(d => ({ ...d, [vid]: { ...draftFor(vid, retail, wholesale), [k]: val } }))

  const srcBadge = (src: string) => {
    if (!src) return null
    const label = src === 'ai_estimate' ? 'auto' : src === 'ai_adjusted' ? 'auto✓' : 'manual'
    const color = src.startsWith('ai') ? C.amber : C.faint
    return <span style={{ fontSize: 9, color, opacity: 0.85, marginLeft: 4 }}>{label}</span>
  }
  const aiBusy = estimateMissing.isPending || estimateOne.isPending || refreshRecs.isPending

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <Tag size={15} style={{ color: C.amber }} />
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Pricing</h2>
        <span style={{ fontSize: 11, color: C.faint }}>retail &amp; wholesale defaults · $ / kg{region ? ` · ${region}` : ''}</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => estimateMissing.mutate()} disabled={aiBusy} style={pillBtn(true)}>
          <Sparkles size={12} /> {estimateMissing.isPending ? 'Estimating…' : 'Estimate missing'}
        </button>
        <button onClick={() => refreshRecs.mutate()} disabled={aiBusy} style={pillBtn(false)}>
          <RefreshCw size={12} /> {refreshRecs.isPending ? 'Checking…' : 'Refresh recommendations'}
        </button>
        <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint, padding: 4 }} aria-label="Toggle pricing">
          <ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>
      </div>

      {notice && (
        <div style={{ fontSize: 12, color: C.muted, background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Sparkles size={13} style={{ color: C.amber, flexShrink: 0, marginTop: 1 }} />
          <span style={{ flex: 1 }}>{notice}</span>
          <button onClick={() => setNotice(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={13} /></button>
        </div>
      )}
      {!region && (
        <div style={{ fontSize: 11, color: C.faint, marginBottom: 10 }}>Tip: set your farm location (Farm Map → plots) so automatic estimates are regional.</div>
      )}

      {/* Recommendations to review */}
      {recs.length > 0 && (
        <div style={{ marginBottom: 14, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)', fontSize: 11, fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {recs.length} price recommendation{recs.length === 1 ? '' : 's'} to review
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {recs.map((r: PriceRecommendation) => {
              const up = r.direction === 'up'
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: `1px solid ${C.border}`, fontSize: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 130 }}>
                    <span style={{ color: C.text, fontWeight: 600 }}>{r.variety_name}</span>
                    <span style={{ color: C.faint, marginLeft: 6, textTransform: 'capitalize' }}>{r.channel}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.muted }}>
                    <span>${centsToStr(r.current_cents_per_kg)}</span>
                    {up ? <TrendingUp size={14} style={{ color: C.green }} /> : <TrendingDown size={14} style={{ color: '#E0A458' }} />}
                    <span style={{ color: up ? C.green : '#E0A458', fontWeight: 700 }}>${centsToStr(r.recommended_cents_per_kg)}</span>
                    <span style={{ color: C.faint }}>/kg</span>
                  </div>
                  <span style={{ flex: 1, minWidth: 160, color: C.faint, fontSize: 11 }}>{r.rationale}{r.confidence ? ` · ${r.confidence} confidence` : ''}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => decideRec.mutate({ id: r.id, action: 'apply' })} disabled={decideRec.isPending} style={{ ...pillBtn(true), padding: '5px 10px' }}><Check size={12} /> Apply</button>
                    <button onClick={() => decideRec.mutate({ id: r.id, action: 'dismiss' })} disabled={decideRec.isPending} style={{ ...pillBtn(false), padding: '5px 10px' }}>Dismiss</button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Price table */}
      {open && (isLoading ? (
        <div style={{ fontSize: 12, color: C.faint, padding: '10px 0' }}>Loading…</div>
      ) : prices.length === 0 ? (
        <div style={{ fontSize: 12, color: C.faint, padding: '10px 0' }}>Add varieties first, then set or auto-estimate their prices.</div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr auto', gap: 8, padding: '0 4px 6px', fontSize: 10, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', position: 'sticky', top: 0, background: C.card }}>
            <div>Variety</div><div>Retail $/kg</div><div>Wholesale $/kg</div><div />
          </div>
          {prices.map(p => {
            const d = draftFor(p.variety_id, p.retail_cents_per_kg, p.wholesale_cents_per_kg)
            const dirty = strToCents(d.retail) !== (p.retail_cents_per_kg ?? null) || strToCents(d.wholesale) !== (p.wholesale_cents_per_kg ?? null)
            return (
              <div key={p.variety_id} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr auto', gap: 8, alignItems: 'center', padding: '5px 4px', borderTop: `1px solid ${C.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{p.variety_name}</span>
                  {p.category && <span style={{ fontSize: 10, color: C.faint }}>{p.category}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input style={{ ...inp, padding: '6px 8px', fontSize: 12 }} type="number" min={0} step={0.01} value={d.retail}
                    onChange={e => setDraft(p.variety_id, 'retail', e.target.value, p.retail_cents_per_kg, p.wholesale_cents_per_kg)} placeholder="—" />
                  {srcBadge(p.retail_source)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input style={{ ...inp, padding: '6px 8px', fontSize: 12 }} type="number" min={0} step={0.01} value={d.wholesale}
                    onChange={e => setDraft(p.variety_id, 'wholesale', e.target.value, p.retail_cents_per_kg, p.wholesale_cents_per_kg)} placeholder="—" />
                  {srcBadge(p.wholesale_source)}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {dirty && (
                    <button onClick={() => setPrice.mutate({ id: p.variety_id, body: { retail_cents_per_kg: strToCents(d.retail), wholesale_cents_per_kg: strToCents(d.wholesale) } })}
                      disabled={setPrice.isPending} style={{ ...pillBtn(true), padding: '5px 9px' }}><Check size={12} /></button>
                  )}
                  <button onClick={() => estimateOne.mutate(p.variety_id)} disabled={aiBusy} title="Auto-estimate (fills empty)" style={{ ...pillBtn(false), padding: '5px 9px' }}><Sparkles size={12} /></button>
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
const pillBtn = (primary: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  background: primary ? C.amber : C.card2, color: primary ? C.bg : C.muted,
  border: `1px solid ${primary ? C.amber : C.border}`,
})

export function VarietiesPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<Variety | null | 'new'>(null)
  const [filterCat, setFilterCat] = useState('All')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['varieties', filterCat],
    queryFn: () => varietiesApi.list(filterCat === 'All' ? undefined : filterCat).then(r => r.data),
  })
  const remove = useMutation({
    mutationFn: (id: string) => varietiesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['varieties'] }),
  })

  const all       = data?.varieties ?? []
  const varieties = search.trim()
    ? all.filter(v => v.name.toLowerCase().includes(search.toLowerCase()) || v.common_name?.toLowerCase().includes(search.toLowerCase()))
    : all

  // Stats
  const catCounts = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = all.filter(v => v.category === cat).length
    return acc
  }, {} as Record<string, number>)
  const withRipening = all.filter(v => v.ripening_start_week)

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg, fontFamily: '"DM Sans", system-ui, sans-serif', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif' }}>Varieties</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>Cultivar catalog — ripening windows, flavor profiles, growing notes</p>
        </div>
        <button onClick={() => setModal('new')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.amber, border: 'none', color: C.bg, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={16} /> Add variety
        </button>
      </div>

      {/* Stat chips */}
      {!isLoading && all.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { label: 'Total varieties', value: all.length, color: C.amber },
            { label: 'With ripening windows', value: withRipening.length, color: C.green },
            { label: 'Categories', value: Object.values(catCounts).filter(c => c > 0).length, color: C.muted },
          ].map(s => (
            <div key={s.label} style={{ padding: '6px 14px', borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.value}</span>
              <span style={{ fontSize: 11, color: C.faint }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Ripening calendar */}
      {!isLoading && <RipeningCalendar varieties={all} />}

      {/* Pricing — retail/wholesale defaults + AI estimates & recommendations */}
      {!isLoading && all.length > 0 && <PricingPanel />}

      {/* Search + category filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search varieties…"
          style={{ ...inp, width: 200, padding: '7px 12px' }}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['All', ...CATEGORIES].map(cat => {
            const count = cat === 'All' ? all.length : catCounts[cat] ?? 0
            if (cat !== 'All' && count === 0) return null
            return (
              <button key={cat} onClick={() => setFilterCat(cat)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: filterCat === cat ? 600 : 400, cursor: 'pointer', background: filterCat === cat ? C.amber : C.card, color: filterCat === cat ? C.bg : C.muted, border: `1px solid ${filterCat === cat ? C.amber : C.border}` }}>
                {cat}{count > 0 && cat !== 'All' && <span style={{ marginLeft: 5, opacity: 0.7 }}>{count}</span>}
              </button>
            )
          })}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: C.faint, fontSize: 14 }}>Loading…</div>
      ) : varieties.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>{search ? 'No varieties match your search.' : 'No varieties yet.'}</p>
          {!search && <><p style={{ fontSize: 12, color: C.faint, margin: '6px 0 16px' }}>Add your first cultivar to track ripening, flavor, and growing notes.</p>
          <button onClick={() => setModal('new')} style={{ padding: '9px 18px', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add first variety</button></>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {varieties.map(v => {
            const catColor = CAT_COLORS[v.category] ?? C.muted
            return (
              <div key={v.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <div
                  onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = C.hover)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Category color dot */}
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: catColor, flexShrink: 0, opacity: 0.9 }} />
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: catColor + '18', color: catColor, flexShrink: 0 }}>{v.category}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.text }}>{v.name}</p>
                    {v.common_name && v.common_name !== v.name && <p style={{ margin: '1px 0 0', fontSize: 12, color: C.muted }}>{v.common_name}</p>}
                  </div>
                  {v.ripening_start_week && (
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: 11, color: C.faint }}>
                        {MONTHS[Math.floor(weekToMonthFrac(v.ripening_start_week))]} –{' '}
                        {MONTHS[Math.min(11, Math.floor(weekToMonthFrac(v.ripening_end_week ?? v.ripening_start_week + 4)))]}
                      </p>
                      {v.average_weight_grams && <p style={{ margin: 0, fontSize: 10, color: C.faint }}>{v.average_weight_grams}g avg</p>}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button onClick={e => { e.stopPropagation(); setModal(v) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint, padding: '4px 6px', borderRadius: 6 }} aria-label={`Edit ${v.name}`}><Pencil size={14} /></button>
                    <button onClick={e => { e.stopPropagation(); if (confirm(`Delete ${v.name}?`)) remove.mutate(v.id) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint, padding: '4px 6px', borderRadius: 6 }} aria-label={`Delete ${v.name}`}><Trash2 size={14} /></button>
                    <ChevronDown size={14} style={{ color: C.faint, transform: expanded === v.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', marginTop: 2 }} />
                  </div>
                </div>
                {expanded === v.id && (
                  <div style={{ padding: '0 16px 14px 16px', borderTop: `1px solid ${C.border}`, paddingTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {([
                      v.species && { label: 'Species', val: v.species, italic: true },
                      v.nursery_source && { label: 'Source', val: v.nursery_source },
                      v.average_weight_grams && { label: 'Avg weight', val: `${v.average_weight_grams}g` },
                      v.ripening_start_week && { label: 'Ripening window', val: `Wk ${v.ripening_start_week}–${v.ripening_end_week ?? '?'} (${MONTHS[Math.floor(weekToMonthFrac(v.ripening_start_week))]}–${MONTHS[Math.min(11, Math.floor(weekToMonthFrac(v.ripening_end_week ?? v.ripening_start_week + 4)))]})` },
                      v.flavor_profile && { label: 'Flavor', val: v.flavor_profile, full: true },
                      v.notes && { label: 'Notes', val: v.notes, full: true },
                    ].filter(Boolean) as { label: string; val: string; italic?: boolean; full?: boolean }[]).map((item) => (
                      <div key={item.label} style={item.full ? { gridColumn: '1 / -1' } : {}}>
                        <p style={{ fontSize: 10, fontWeight: 600, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 2px' }}>{item.label}</p>
                        <p style={{ fontSize: 12, color: C.muted, margin: 0, fontStyle: item.italic ? 'italic' : 'normal', lineHeight: 1.5 }}>{item.val}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {modal !== null && (
        <VarietyModal variety={modal === 'new' ? null : modal} onClose={() => setModal(null)} />
      )}
    </main>
  )
}
